import { z } from "zod";
/**
 * Delivery zones — the one definition of a valid zone, shared by the CRM map
 * editor and the route body so the two cannot drift (the `promotions.ts`
 * Pattern-B precedent: `safeParse`d in the browser AND attached as the Fastify
 * body schema). Messages are Spanish because they surface verbatim in the editor.
 *
 * A zone is a polygon a shop draws on a map plus the delivery fee it charges
 * inside it. Every rule here bounds a value that ends up deciding whether a real
 * person is told "sí te lo llevamos" and what they are charged for it.
 */
/** Per zone, across every ring. A hand-drawn delivery area needs dozens, not thousands. */
export const MAX_ZONE_VERTICES = 500;
export const MAX_ZONE_NAME_LENGTH = 80;
/** Per client. A shop with more than this is describing streets, not zones. */
export const MAX_DELIVERY_ZONES = 50;
/**
 * The fee ceiling. Not a claim about what delivery costs — the blast radius of
 * a misplaced decimal point in a field that sets what a customer pays
 * ($10,000.00 MXN).
 */
export const MAX_ZONE_FEE_CENTS = 1_000_000;
/** What the CRM's "Probar una dirección" box and the map search accept. */
export const MAX_COVERAGE_QUERY_LENGTH = 300;
/** GeoJSON order: `[longitude, latitude]`. Getting this backwards puts Querétaro in the Indian Ocean. */
const positionSchema = z.tuple([
    z.number().min(-180).max(180),
    z.number().min(-90).max(90),
]);
/**
 * A closed ring: at least a triangle (3 distinct points + the closing repeat),
 * first position equal to the last. Leaflet and Geoman both emit closed rings;
 * the check exists so a hand-built body cannot store an open one that the
 * point-in-polygon test would read differently from the map that drew it.
 */
const linearRingSchema = z
    .array(positionSchema)
    .min(4, "Una zona necesita al menos tres puntos")
    .refine((ring) => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    return first !== undefined && last !== undefined && first[0] === last[0] && first[1] === last[1];
}, "El contorno de la zona debe cerrarse");
/** Outer ring first, then holes — the GeoJSON convention. */
const polygonCoordinatesSchema = z.array(linearRingSchema).min(1, "La zona no tiene contorno");
export const zoneGeometrySchema = z
    .discriminatedUnion("type", [
    z.object({ type: z.literal("Polygon"), coordinates: polygonCoordinatesSchema }),
    z.object({
        type: z.literal("MultiPolygon"),
        coordinates: z.array(polygonCoordinatesSchema).min(1, "La zona no tiene contorno"),
    }),
])
    .refine((g) => countVertices(g) <= MAX_ZONE_VERTICES, `La zona tiene demasiados puntos (máximo ${MAX_ZONE_VERTICES}) — simplifica el contorno`);
export function countVertices(geometry) {
    const polygons = geometry.type === "Polygon"
        ? [geometry.coordinates]
        : geometry.coordinates;
    return polygons.reduce((sum, rings) => sum + rings.reduce((s, ring) => s + ring.length, 0), 0);
}
export const deliveryZoneInputSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, "El nombre es obligatorio")
        .max(MAX_ZONE_NAME_LENGTH, `Máximo ${MAX_ZONE_NAME_LENGTH} caracteres`),
    /**
     * Minor units, like every money field. `0` = free delivery in this zone, and
     * the agent may say so. There is no `null`: a zone with no fee is not a zone
     * the system can quote, and a quote is what this column exists to make.
     */
    feeCents: z
        .number()
        .int("El costo de envío debe ser un número entero de centavos")
        .min(0, "El costo de envío no puede ser negativo")
        .max(MAX_ZONE_FEE_CENTS, "El costo de envío parece demasiado alto — revisa el monto"),
    geometry: zoneGeometrySchema,
});
export const deliveryZoneActiveSchema = z.object({ isActive: z.boolean() });
export const coverageQuerySchema = z.object({
    query: z
        .string()
        .trim()
        .min(1, "Escribe una dirección")
        .max(MAX_COVERAGE_QUERY_LENGTH, `Máximo ${MAX_COVERAGE_QUERY_LENGTH} caracteres`),
});
//# sourceMappingURL=delivery-zones.js.map